import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, ChevronsLeft, ChevronsRight, PanelRightClose, SendHorizontal, Trash2, User } from 'lucide-react';
import { useChatPanel } from '@/stores/useChatPanel';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAgentStore, type AgentStatus, type UIAction } from '@/agent/agentStore';
import { useAgentBridge } from '@/agent/AgentBridgeProvider';
import { simulateAgentResponse } from '@/agent/simulateResponse';
import { agentChat, isAgentBackendAvailable } from '@/integrations/api/agent_chat_api';
import { useTenant } from '@/contexts/TenantContext';
import { EXAMPLE_PROMPTS } from '@/agent/fakeBrain';
import { AgentWidgetRenderer } from '@/agent/widgets/AgentWidgets';
import { AgentRecommendationChip } from '@/agent/components/AgentRecommendationChip';
import { AgentCapabilitiesPanel } from '@/components/agent/AgentCapabilitiesPanel';
import { ConversationHistory } from '@/components/agent/ConversationHistory';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAgentMemory } from '@/hooks/useAgentMemory';

/**
 * Per-route contextual quick-action prompts. The first match wins;
 * `default` is used as the global fallback (Dashboard and others).
 */
const CONTEXTUAL_SUGGESTIONS: Array<{ test: (p: string) => boolean; prompts: string[] }> = [
  {
    test: (p) => p.startsWith('/production'),
    prompts: [
      'Ver WOs pendientes',
      'Analizar OEE de la línea',
      '¿Qué orden tiene mayor retraso?',
      'Resumen de turno actual',
    ],
  },
  {
    test: (p) => p.startsWith('/quality'),
    prompts: [
      'Ver NCs abiertas',
      'Tendencia de defectos últimas 24h',
      'Top defectos por línea',
      '¿Hay alertas críticas de calidad?',
    ],
  },
  {
    test: (p) => p.startsWith('/maintenance'),
    prompts: [
      'Equipos con mantenimiento próximo',
      'RUL de la máquina crítica',
      'Alertas de condition monitoring',
      'Plan preventivo de esta semana',
    ],
  },
  {
    test: (p) => p.startsWith('/mlops'),
    prompts: [
      '¿Hay drift activo?',
      'Modelos por debajo de F1 objetivo',
      'Últimos experimentos',
      'Resumen de calibración',
    ],
  },
];

function useContextualSuggestions(pathname: string): string[] {
  return useMemo(() => {
    const match = CONTEXTUAL_SUGGESTIONS.find((s) => s.test(pathname));
    if (match) return match.prompts.slice(0, 4);
    return EXAMPLE_PROMPTS.slice(0, 4);
  }, [pathname]);
}

const STATUS_META: Record<AgentStatus, { label: string; dot: string; pulse: boolean }> = {
  connected: { label: 'Conectado', dot: 'bg-[hsl(var(--success))]', pulse: false },
  processing: { label: 'Procesando', dot: 'bg-amber-500', pulse: true },
  error: { label: 'Error', dot: 'bg-destructive', pulse: false },
};

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function AgentChat({ onClose }: { onClose?: () => void } = {}) {
  const messages = useAgentStore((s) => s.messages);
  const isTyping = useAgentStore((s) => s.isTyping);
  const status = useAgentStore((s) => s.status);
  const setStatus = useAgentStore((s) => s.setStatus);
  const sendUserMessage = useAgentStore((s) => s.sendUserMessage);
  const clearHistory = useAgentStore((s) => s.clearHistory);

  const appendAgentMessage = useAgentStore((s) => s.appendAgentMessage);
  const setTyping = useAgentStore((s) => s.setTyping);
  const { role } = useTenant();

  const expand = useChatPanel((s) => s.expand);
  const shrink = useChatPanel((s) => s.shrink);
  const size = useChatPanel((s) => s.size);
  const canExpand = size !== 45;
  const canShrink = size !== 22;

  const { executeActions } = useAgentBridge();

  const location = useLocation();
  const suggestions = useContextualSuggestions(location.pathname);
  const statusMeta = STATUS_META[status];

  const [input, setInput] = useState('');
  const [tab, setTab] = useState<'chat' | 'history'>('chat');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const memory = useAgentMemory();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const viewport = node.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [messages, isTyping]);

  // Listen for "Yes" clicks on recommendation chips
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent<{ id: string; acceptText?: string }>).detail;
      const text = detail?.acceptText;
      if (!text) return;
      try {
        await simulateAgentResponse(text, executeActions);
      } catch (err) {
        console.error('[AgentChat] rec-accept error', err);
      }
    };
    window.addEventListener('agent:rec-accept', handler as EventListener);
    return () => window.removeEventListener('agent:rec-accept', handler as EventListener);
  }, [executeActions]);

  const runLocalBrain = async (content: string) => {
    await simulateAgentResponse(content, executeActions);
    try {
      const last = useAgentStore.getState().messages.slice().reverse().find((m) => m.role === 'agent');
      if (last?.content) memory.addMessage({ role: 'assistant', content: last.content });
    } catch {}
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || isTyping) return;
    setInput('');
    sendUserMessage(content);
    try {
      memory.addMessage({ role: 'user', content });
    } catch {}

    // Prefer the real backend agent (Gemini + platform catalog + tools). Fall
    // back to the local brain if the backend is unavailable / not configured.
    if (isAgentBackendAvailable()) {
      setTyping(true);
      try {
        const history = useAgentStore
          .getState()
          .messages.filter((m) => m.role === 'user' || m.role === 'agent')
          .slice(-12)
          .map((m) => ({ role: (m.role === 'agent' ? 'assistant' : 'user') as 'user' | 'assistant', content: m.content }));

        const res = await agentChat({
          messages: history,
          screen_context: { route: location.pathname, filters: {}, selectedEntity: null },
          user_role: role ?? 'operator',
        });

        appendAgentMessage({ content: res.reply, uiActions: res.ui_actions as UIAction[] });
        try {
          if (res.reply) memory.addMessage({ role: 'assistant', content: res.reply });
        } catch {}
        if (Array.isArray(res.ui_actions) && res.ui_actions.length > 0) {
          await executeActions(res.ui_actions as UIAction[]);
        }
        setTyping(false);
        setStatus('connected');
        return;
      } catch (err) {
        console.warn('[AgentChat] backend unavailable, using local brain', err);
        setTyping(false);
        // fall through to the local brain below
      }
    }

    try {
      await runLocalBrain(content);
      setStatus('connected');
    } catch (err) {
      console.error('[AgentChat] response error', err);
      useAgentStore.getState().setTyping(false);
      setStatus('error');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClear = () => {
    clearHistory();
    toast.success('Historial borrado');
  };

  const handleSuggestion = (text: string) => {
    setInput(text);
  };

  return (
    <div className="h-full w-full flex flex-col min-h-0 overflow-hidden bg-background">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-2 border-b bg-card px-3 py-2">
        <div className="relative">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-4 w-4 text-primary" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">Agente EXPAI</div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title={`Estado: ${statusMeta.label}`}>
            <span className="relative inline-flex h-1.5 w-1.5">
              {statusMeta.pulse && (
                <span className={`absolute inline-flex h-full w-full rounded-full ${statusMeta.dot} opacity-60 animate-ping`} />
              )}
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${statusMeta.dot} ${statusMeta.pulse ? 'animate-pulse' : ''}`} />
            </span>
            {statusMeta.label}
          </div>
        </div>
        <AgentCapabilitiesPanel />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={shrink}
          disabled={!canShrink}
          title="Reducir panel"
          aria-label="Reducir panel del agente"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={expand}
          disabled={!canExpand}
          title="Ampliar panel"
          aria-label="Ampliar panel del agente"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClear} title="Borrar historial" aria-label="Borrar historial del chat">
          <Trash2 className="h-4 w-4" />
        </Button>
        {onClose && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} title="Cerrar agente" aria-label="Cerrar panel del agente">
            <PanelRightClose className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Tabs: Chat / Historial */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'chat' | 'history')} className="flex-1 min-h-0 flex flex-col">
        <TabsList className="mx-2 mt-2 grid grid-cols-2 h-8">
          <TabsTrigger value="chat" className="text-xs h-7">Chat</TabsTrigger>
          <TabsTrigger value="history" className="text-xs h-7">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="flex-1 min-h-0 flex flex-col mt-0 data-[state=inactive]:hidden">
          {/* Body */}
          <ScrollArea ref={scrollRef} className="flex-1 min-h-0">
            <div className="p-3 space-y-3">
              {messages.length === 0 ? (
                <EmptyState onPick={handleSuggestion} suggestions={suggestions} />
              ) : (
                messages.map((m) => <MessageBubble key={m.id} message={m} />)
              )}
              {isTyping && <TypingIndicator />}
            </div>
          </ScrollArea>

          {/* Footer */}
          <form onSubmit={handleSubmit} className="shrink-0 border-t bg-card p-2 flex items-center gap-2" role="search" aria-label="Enviar mensaje al agente">
            <label htmlFor="expai-chat-input" className="sr-only">Mensaje para el agente</label>
            <Input
              id="expai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe o pregunta al agente..."
              disabled={isTyping}
              className="h-9 text-sm"
              aria-label="Escribe tu mensaje para el agente"
            />
            <Button type="submit" size="icon" className="h-9 w-9 shrink-0" disabled={isTyping || !input.trim()} aria-label="Enviar mensaje">
              <SendHorizontal className="h-4 w-4" />
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="history" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
          <ConversationHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function MessageBubble({ message }: { message: ReturnType<typeof useAgentStore.getState>['messages'][number] }) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {message.content}
        </span>
      </div>
    );
  }

  const isUser = message.role === 'user';
  const isStreaming = message.status === 'streaming';

  return (
    <div className={`flex flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}>
      <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'} w-full`}>
        {!isUser && (
          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <Bot className="h-3.5 w-3.5 text-primary" />
          </div>
        )}
        <div
          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isUser
              ? 'bg-primary/10 text-foreground'
              : 'bg-card border text-foreground'
          }`}
        >
          {message.content}
          {isStreaming && <span className="inline-block w-1.5 h-3 ml-0.5 bg-primary/60 align-middle animate-pulse" />}
          {!isStreaming && message.widgets && message.widgets.length > 0 && (
            <div className="space-y-1">
              {message.widgets.map((w, i) => (
                <AgentWidgetRenderer key={i} widget={w} messageId={message.id} />
              ))}
            </div>
          )}
          {!isStreaming && message.recommendation && (
            <AgentRecommendationChip
              recommendation={message.recommendation}
              onAccept={(rec) => {
                const acceptText = message.recommendation!.acceptText;
                const store = useAgentStore.getState();
                store.updateAgentMessage(message.id, { recommendation: null });
                store.sendUserMessage(acceptText);
                window.dispatchEvent(
                  new CustomEvent('agent:rec-accept', { detail: { id: rec.id, acceptText } }),
                );
              }}
              onDismiss={() => {
                useAgentStore.getState().updateAgentMessage(message.id, { recommendation: null });
              }}
            />
          )}
        </div>
        {isUser && (
          <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </div>
      <span
        className={`text-[10px] text-muted-foreground/70 px-8 ${isUser ? 'text-right' : 'text-left'}`}
        title={new Date(message.timestamp).toLocaleString()}
      >
        {formatTime(message.timestamp)}
      </span>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2 justify-start">
      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Bot className="h-3.5 w-3.5 text-primary" />
      </div>
      <div className="bg-card border rounded-lg px-3 py-2 flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '300ms' }} />
        <span className="ml-1.5 text-xs text-muted-foreground">pensando…</span>
      </div>
    </div>
  );
}

function EmptyState({ onPick, suggestions }: { onPick: (text: string) => void; suggestions?: string[] }) {
  const prompts = (suggestions && suggestions.length > 0 ? suggestions : EXAMPLE_PROMPTS).slice(0, 4);
  return (
    <div className="flex flex-col items-center text-center pt-6 px-2 gap-4">
      <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
        <Bot className="h-7 w-7 text-primary opacity-60" />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-medium">Soy tu agente en planta.</div>
        <div className="text-xs text-muted-foreground">Sugerencias para esta sección:</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
        {prompts.map((p) => (
          <Button
            key={p}
            variant="outline"
            size="sm"
            className="h-auto py-2 px-2 text-xs text-left justify-start whitespace-normal leading-snug"
            onClick={() => onPick(p)}
          >
            {p}
          </Button>
        ))}
      </div>
    </div>
  );
}
